import { useCallback, useEffect, useState } from 'react';
import aiAssistantFabIcon from '../../icons/Icon-AI-Assistant-FAB.svg';
import closeIcon from '../../icons/Icon-AI-Scanner-Close.svg';
import { useFeatureToggle } from '../../utils/hooks/useFeatureToggle';

const FAB_BOTTOM_OFFSET = 34;
const MOBILE_FOOTER_ID = 'gnavi-mobile-footer';

/**
 * AiScannerContainer Component
 *
 * Renders a fixed floating "AI アシスタント" button at the bottom-right of the
 * Scanner page at the smartphone breakpoint. Tapping the button opens the AI
 * Scanner chat overlay. The overlay can be dismissed via the ✕ icon at the
 * top-right.
 *
 * While the footer is not in view the button stays fixed at the bottom of the
 * viewport (bottom: 34px via CSS). Once the footer scrolls into view the
 * `bottom` value is increased so the button maintains the same 34px gap from
 * the top of the footer instead of from the viewport edge.
 *
 * @returns {JSX.Element} The AI Scanner FAB and, when open, the overlay.
 */
export const AiScannerContainer = () => {
  const { isFeatureEnabled } = useFeatureToggle('aiScannerPopsicleEnabled');
  const [isOpen, setIsOpen] = useState(false);
  const [fabBottomOverride, setFabBottomOverride] = useState(null);

  useEffect(() => {
    if (!isFeatureEnabled) { return; }

    let rafId = null;

    const updateFabPosition = () => {
      const footer = document.getElementById(MOBILE_FOOTER_ID);
      if (!footer) return;

      const footerRect = footer.getBoundingClientRect();
      if (footerRect.top < window.innerHeight) {
        setFabBottomOverride(window.innerHeight - footerRect.top + FAB_BOTTOM_OFFSET);
      } else {
        setFabBottomOverride(null);
      }
    };

    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        updateFabPosition();
        rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateFabPosition();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [isFeatureEnabled]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  if (!isFeatureEnabled) {
    return null;
  }

  return (
    <div className="ai-scanner-container">
      <button
        className="ai-scanner-fab"
        style={fabBottomOverride !== null ? { bottom: `${fabBottomOverride}px` } : undefined}
        onClick={handleOpen}
        aria-label="AIアシスタントを開く"
        aria-expanded={isOpen}
      >
        <img
          src={aiAssistantFabIcon}
          alt=""
          aria-hidden="true"
          width="68"
          height="68"
        />
      </button>
      {isOpen && (
        <div
          className="ai-scanner-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="AIスキャナー"
        >
          <button
            className="ai-scanner-overlay-close"
            onClick={handleClose}
            aria-label="閉じる"
          >
            <img
              src={closeIcon}
              alt=""
              aria-hidden="true"
              width="16"
              height="16"
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default AiScannerContainer;
